import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import MobileHeader from "../../components/MobileHeader";
import BottomNav from "../../components/BottomNav";
import { Card, Button, StatusBadge } from "../../components/ui-kit";
import { FileArrowUp, CheckCircle, XCircle, Hourglass, FilePdf, Image as ImageIcon } from "@phosphor-icons/react";

const DOC_KINDS = [
  { key: "driver_license", i18n: "driver.doc_driver_license" },
  { key: "vehicle_registration", i18n: "driver.doc_vehicle_registration" },
  { key: "id_card", i18n: "driver.doc_id_card" },
  { key: "vehicle_plate_photo", label: "Photo plaque rouleur" },
  { key: "insurance", i18n: "driver.doc_insurance" },
];

export default function DriverDocuments() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const workerRole = user?.role === "courier" ? "courier" : "driver";
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(null);
  const refs = useRef({});

  const load = async () => {
    try {
      const { data } = await api.get("/driver/documents");
      setDocs(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  const upload = async (kind, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Max 10 MB");
      return;
    }
    setUploading(kind);
    const fd = new FormData();
    fd.append("doc_type", kind);
    fd.append("file", file);
    try {
      await api.post("/driver/documents/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(t("driver.doc_upload_success"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(null);
    }
  };

  const byType = {};
  docs.forEach((d) => { byType[d.doc_type] = d; });

  return (
    <div className="phone-frame phone-scroll-safe bg-premium-grad">
      <MobileHeader title={t("driver.documents_title")} />
      <div className="space-y-4 px-4 pt-2">
        <div>
          <h1 className="font-heading text-2xl font-bold">{t("driver.documents_title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("driver.documents_subtitle")}</p>
        </div>

        {DOC_KINDS.map((k) => {
          const d = byType[k.key];
          const isImage = d?.content_type?.startsWith("image/");
          const isPdf = d?.content_type === "application/pdf";
          const Icon = isPdf ? FilePdf : ImageIcon;
          return (
            <Card key={k.key} className="p-4" data-testid={`doc-card-${k.key}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className={`rounded-xl p-2 ${
                    d?.status === "APPROVED" ? "bg-secondary/15 text-secondary" :
                    d?.status === "REJECTED" ? "bg-destructive/15 text-destructive" :
                    d ? "bg-primary/15 text-primary" : "bg-white/5 text-muted-foreground"
                  }`}>
                    {d?.status === "APPROVED" ? <CheckCircle size={20} weight="fill" /> :
                     d?.status === "REJECTED" ? <XCircle size={20} weight="fill" /> :
                     d ? <Hourglass size={20} weight="bold" /> : <FileArrowUp size={20} weight="bold" />}
                  </div>
                  <div className="min-w-0">
                    <div className="font-heading font-bold">{k.label || t(k.i18n)}</div>
                    {["driver_license", "vehicle_registration", "id_card", "vehicle_plate_photo"].includes(k.key) && (
                      <div className="text-[9px] font-bold uppercase tracking-widest text-primary">Obligatoire</div>
                    )}
                    {d ? (
                      <div className="mt-0.5">
                        <StatusBadge status={d.status} />
                        {d.review_note && (
                          <div className="mt-1 text-[11px] text-destructive">{d.review_note}</div>
                        )}
                      </div>
                    ) : (
                      <div className="text-[11px] text-muted-foreground">Non téléversé</div>
                    )}
                  </div>
                </div>
              </div>

              {d && (
                <div className="mt-3 flex items-center gap-2 rounded-xl bg-white/5 p-2 text-[11px]">
                  <Icon size={14} weight="bold" className="shrink-0 text-muted-foreground" />
                  <span className="truncate">{d.original_filename}</span>
                  <span className="ml-auto font-mono text-[10px] text-muted-foreground">
                    {Math.round(d.size / 1024)} KB
                  </span>
                </div>
              )}

              <input
                ref={(el) => (refs.current[k.key] = el)}
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                className="hidden"
                onChange={(e) => upload(k.key, e.target.files?.[0])}
                data-testid={`doc-file-${k.key}`}
              />
              <Button
                variant={d ? "outline" : "primary"}
                className="mt-3 w-full"
                onClick={() => refs.current[k.key]?.click()}
                disabled={uploading === k.key}
                data-testid={`doc-upload-${k.key}`}
              >
                <FileArrowUp weight="bold" />
                {uploading === k.key ? "Envoi..." : d ? "Remplacer" : t("driver.upload")}
              </Button>
            </Card>
          );
        })}
      </div>
      <BottomNav role={workerRole} />
    </div>
  );
}
